import {
  JIRA_GROUP_ORDER,
  JIRA_PRIORITY_COLOR,
  jiraModuleColor,
  jiraPriorityLevel,
  jiraStatusColor,
  jiraStatusKey,
  jiraTitleParts,
  type JiraIssueCard,
  type JiraStatusColorKey,
} from '@novachat/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, Search } from 'lucide-react';
import { useState } from 'react';
import { create } from 'zustand';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useLive } from '../state/live';
import { hideThreadsPage, useThreadPanel } from '../state/thread';
import { useJiraIssue } from './JiraIssueDialog';
import { useJira } from './JiraSettings';

type List = 'assigned' | 'reported' | 'sprint';
const TABS: [List, string][] = [
  ['assigned', 'Atananlar'],
  ['reported', 'Açtıklarım'],
  ['sprint', 'Sprint'],
];

/** "Benim işlerim" (docs/plans/2026-11-04-jira-design.md §5, §7), in the main area like "Dallar". */
export const useJiraPage = create<{ shown: boolean; show(): void; hide(): void }>()((set) => ({
  shown: false,
  show: () => {
    useLive.getState().select(null);
    hideThreadsPage();
    set({ shown: true });
  },
  hide: () => set({ shown: false }),
}));
// Opening a channel or "Dallar" leaves the page.
useLive.subscribe((state, before) => {
  if (state.selectedChannelId && state.selectedChannelId !== before.selectedChannelId) useJiraPage.getState().hide();
});
useThreadPanel.subscribe((state) => {
  if (state.page) useJiraPage.getState().hide();
});

export interface JiraGroup {
  /** The status's name, as Jira has it. */
  name: string;
  color: JiraStatusColorKey;
  cards: JiraIssueCard[];
}

/** Issues grouped by status, what is moving first (Devam ediyor → Test → Bilgi bekliyor → Açık → Yapılacak). */
export function byStatus(cards: readonly JiraIssueCard[]): JiraGroup[] {
  const groups = new Map<string, JiraGroup>();
  for (const card of cards) {
    const group = groups.get(card.status.name) ?? { name: card.status.name, color: jiraStatusKey(card.status), cards: [] };
    group.cards.push(card);
    groups.set(card.status.name, group);
  }
  return [...groups.values()].sort((a, b) => JIRA_GROUP_ORDER.indexOf(a.color) - JIRA_GROUP_ORDER.indexOf(b.color));
}

const matches = (card: JiraIssueCard, query: string) => !query || `${card.key} ${card.summary}`.toLocaleLowerCase('tr').includes(query);

/** The sidebar's way in, under "Dallar"; only with Jira connected. Its number is what the page last read. */
export function JiraEntry() {
  const jira = useJira();
  const shown = useJiraPage((s) => s.shown);
  const assigned = useQuery({ queryKey: ['jira-mine', 'assigned', null, null], queryFn: () => client.api.jiraMine('assigned'), enabled: false });
  if (!jira.data?.connection) return null;
  return (
    <button
      type="button"
      onClick={() => useJiraPage.getState().show()}
      aria-current={shown ? 'page' : undefined}
      className={`nc-press flex w-full items-center gap-2 rounded-sm border-0 px-2.5 py-[7px] text-left text-[14px] hover:bg-surface-2 hover:text-fg-1 max-md:py-3 ${shown ? 'bg-surface-3 font-semibold text-fg-1' : 'bg-transparent text-fg-2'}`}
    >
      <span aria-hidden="true" className="grid size-[17px] place-items-center rounded-[4px] bg-[#1868DB] text-[10px] font-bold text-white">
        J
      </span>
      <span className="flex-1">Benim işlerim</span>
      {assigned.data && <span className="text-[12px] font-normal text-fg-3 tabular-nums">{assigned.data.length}</span>}
    </button>
  );
}

/** Priority as three bars, as the design draws it. */
export function PriorityBars({ name }: { name: string | null }) {
  const level = jiraPriorityLevel(name);
  return (
    <span aria-hidden="true" className="flex h-[11px] flex-none items-end gap-[2px]">
      {[5, 8, 11].map((height, i) => (
        <span key={height} style={{ height, backgroundColor: i < level ? JIRA_PRIORITY_COLOR : '#353A44' }} className="w-[3px] rounded-[1px]" />
      ))}
    </span>
  );
}

/** The status as a ring: dashed and empty while nobody has started, filled once it moves. */
export function StatusRing({ status, size = 16 }: { status: JiraIssueCard['status']; size?: number }) {
  const key = jiraStatusKey(status);
  const color = jiraStatusColor(status).dot;
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, borderColor: color, borderStyle: key === 'gray' ? 'dashed' : 'solid' }}
      className="grid flex-none place-items-center rounded-full border-[1.8px]"
    >
      {key !== 'gray' && <span style={{ backgroundColor: color, width: size / 2 - 1, height: size / 2 - 1 }} className="rounded-full" />}
    </span>
  );
}

const TABLE = 'md:grid md:grid-cols-[16px_96px_minmax(0,1fr)_150px_96px_88px] md:items-center md:gap-3.5';

function Row({ card }: { card: JiraIssueCard }) {
  const parts = jiraTitleParts(card.summary, card.epic ?? null);
  const module = parts.module && <span style={{ color: jiraModuleColor(parts.module) }}>{parts.module}</span>;
  return (
    <button
      type="button"
      onClick={() => useJiraIssue.getState().show(card.key, card)}
      title={`${card.key} ${card.summary}`}
      aria-label={`${card.key} ${parts.title}, ${card.status.name}`}
      className={`flex w-full gap-3.5 border-0 border-b border-line bg-transparent px-4 py-3 text-left hover:bg-white/[0.03] md:h-[42px] md:px-7 md:py-0 ${TABLE}`}
    >
      <span className="pt-px md:pt-0">
        <StatusRing status={card.status} />
      </span>
      {/* Phones: key, module, parent and priority on a line, the title under them. */}
      <span className="flex min-w-0 flex-1 flex-col gap-1 md:hidden">
        <span className="flex min-w-0 items-center gap-2 text-[12px]">
          <span className="font-mono text-[12.5px] font-semibold text-fg-1">{card.key}</span>
          {module && <span className="truncate font-medium">{module}</span>}
          <span className="flex-1" />
          {parts.ref && <span className="font-mono text-[11px] whitespace-nowrap text-fg-3">↳ {parts.ref}</span>}
          <PriorityBars name={card.priority} />
        </span>
        <span className="line-clamp-2 text-[15px] leading-[1.36] font-medium text-fg-body">{parts.title}</span>
      </span>
      {/* Wider: a table row. */}
      <span className="hidden font-mono text-[13px] font-semibold text-fg-1 md:block">{card.key}</span>
      <span className="hidden truncate text-[14px] text-fg-body md:block">{parts.title}</span>
      <span className="hidden truncate text-[12.5px] font-medium text-fg-3 md:block">{module ?? '—'}</span>
      <span className="hidden font-mono text-[12px] text-fg-3 md:block">{parts.ref ?? '—'}</span>
      <span className="hidden items-center gap-1.5 text-[12.5px] text-fg-3 md:flex">
        <PriorityBars name={card.priority} />
        <span className="truncate">{card.priority ?? '—'}</span>
      </span>
    </button>
  );
}

function Toggle({ on, onChange, label }: { on: boolean; onChange: (on: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} className="flex items-center gap-2 border-0 bg-transparent p-0 text-[13px] text-fg-2">
      {label}
      <span className={`relative h-5 w-[34px] rounded-full transition-colors ${on ? 'bg-brand-500' : 'bg-surface-3'}`}>
        <span className={`absolute top-0.5 size-4 rounded-full bg-white transition-[left] ${on ? 'left-4' : 'left-0.5'}`} />
      </span>
    </button>
  );
}

const projectOf = (card: JiraIssueCard) => card.key.slice(0, card.key.lastIndexOf('-'));
const LAST_PROJECT = 'novachat.jira.project';
const remembered = () => {
  try {
    return localStorage.getItem(LAST_PROJECT);
  } catch {
    return null;
  }
};

export function JiraPage() {
  const [list, setList] = useState<List>('assigned');
  const jira = useJira();
  const me = jira.data?.connection?.accountId ?? null;
  const projects = useQuery({ queryKey: ['jira-projects'], queryFn: () => client.api.jiraProjects(), enabled: list === 'sprint', staleTime: 5 * 60_000 });
  // The sprint is one project's; the other lists show every project, or the one picked.
  const [sprintProject, setSprintProject] = useState(() => remembered() ?? 'NOV');
  const [pickedProject, setPickedProject] = useState<string | null>(null);
  const [onlyMine, setOnlyMine] = useState(true);
  const sprints = useQuery({ queryKey: ['jira-sprints', sprintProject], queryFn: () => client.api.jiraSprints(sprintProject), enabled: list === 'sprint', staleTime: 60_000 });
  const [sprintId, setSprintId] = useState<number | null>(null);
  const sprint = sprints.data?.find((s) => s.id === sprintId) ?? sprints.data?.[0] ?? null;
  const isSprint = list === 'sprint';
  const issues = useQuery({
    queryKey: ['jira-mine', list, isSprint ? sprintProject : null, isSprint ? (sprint?.id ?? null) : null],
    queryFn: () => client.api.jiraMine(list, isSprint ? sprintProject : undefined, isSprint ? sprint?.id : undefined),
    enabled: !isSprint || !sprints.isPending,
    staleTime: 30_000,
  });
  // Which projects the list has, most issues first ("NOV 22", "DES 2").
  const listProjects = isSprint
    ? []
    : [...(issues.data ?? []).reduce((counts, card) => counts.set(projectOf(card), (counts.get(projectOf(card)) ?? 0) + 1), new Map<string, number>())].sort((a, b) => b[1] - a[1]);
  const project = !isSprint && pickedProject && listProjects.some(([key]) => key === pickedProject) ? pickedProject : null;
  const done = useQuery({
    queryKey: ['jira-mine', 'done', list, project],
    queryFn: () => client.api.jiraDone(list as 'assigned' | 'reported', project ?? undefined),
    enabled: !isSprint,
    staleTime: 60_000,
  });
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  // The sprint has its finished issues in the list: they are counted (and shown) from there.
  const scoped = (issues.data ?? []).filter((card) => (isSprint ? !onlyMine || card.assignee?.accountId === me : !project || projectOf(card) === project));
  const open = scoped.filter((card) => card.status.category !== 'done');
  const groups = byStatus(scoped);
  const q = query.trim().toLocaleLowerCase('tr');
  const shown = groups
    .filter((g) => !status || g.name === status)
    .map((g) => ({ ...g, found: g.cards.filter((card) => matches(card, q)) }))
    .filter((g) => !q || g.found.length > 0);
  const finished = isSprint ? scoped.length - open.length : (done.data?.done ?? 0);
  const grand = finished + open.length;
  const hasProgress = grand > 0 && (isSprint ? !!issues.data : !!done.data);

  const chip = (on: boolean) => `nc-press flex h-[30px] flex-none items-center gap-1.5 rounded-md border px-[11px] text-[13px] font-medium ${on ? 'border-white/20 bg-white/[0.09] text-fg-1' : 'border-white/[0.08] bg-transparent text-fg-2 hover:text-fg-1'}`;
  const projectChip = (on: boolean) => `nc-press flex h-[30px] flex-none items-center gap-1.5 rounded-full border px-3 text-[13px] font-semibold ${on ? 'border-brand-500/60 bg-brand-500/15 text-fg-1' : 'border-white/[0.08] bg-transparent text-fg-2 hover:text-fg-1'}`;
  const projectChips: { key: string | null; label: string; count?: number; title?: string }[] = isSprint
    ? (projects.data ?? [{ key: sprintProject, name: sprintProject, issueTypes: [] }]).map((p) => ({ key: p.key, label: p.key, title: p.name }))
    : listProjects.length > 1
      ? [{ key: null, label: 'Tüm projeler', count: issues.data?.length ?? 0 }, ...listProjects.map(([key, count]) => ({ key, label: key, count }))]
      : [];

  return (
    <section aria-labelledby="jira-page-title" className="flex min-h-0 flex-1 flex-col">
      <header className="flex flex-none flex-wrap items-center gap-x-3.5 gap-y-3 px-3 pt-4 md:px-7 md:pt-[22px]">
        <button type="button" aria-label="Listeye dön" onClick={() => useJiraPage.getState().hide()} className="nc-press -mr-2 grid size-9 place-items-center rounded-sm border-0 bg-transparent text-brand-400 md:hidden">
          <ChevronLeft size={22} />
        </button>
        <h1 id="jira-page-title" className="m-0 font-heading text-[22px] font-bold tracking-[-0.015em]">
          Benim işlerim
        </h1>
        {issues.data && <span className="text-[13px] text-fg-3 tabular-nums">{open.length} açık iş</span>}
        <span className="flex-1" />
        {hasProgress && (
          <div className="flex items-center gap-3 max-md:order-last max-md:w-full max-md:flex-col-reverse max-md:items-stretch max-md:gap-2" aria-label={`${finished} / ${grand} tamamlandı`}>
            <div className="flex h-1.5 gap-[2px] overflow-hidden rounded-full bg-[#1f232a] md:w-[180px]">
              <span style={{ flex: finished, backgroundColor: '#68CA80' }} />
              {groups
                .filter((g) => g.cards[0]!.status.category !== 'done')
                .map((g) => (
                  <span key={g.name} style={{ flex: g.cards.length, backgroundColor: g.color === 'gray' ? '#3A3F49' : jiraStatusColor(g.cards[0]!.status).dot }} />
                ))}
            </div>
            <div className="flex items-baseline gap-1.5 whitespace-nowrap tabular-nums">
              <span className="text-[14px] font-semibold text-[#6FD087]">{finished}</span>
              <span className="text-[13px] text-fg-3">/ {grand} tamamlandı</span>
              <span className="flex-1 md:hidden" />
              <span className="ml-1 text-[13px] text-fg-3">%{Math.round((finished / grand) * 100)}</span>
            </div>
          </div>
        )}
        <label className="flex h-[34px] items-center gap-2 rounded-[9px] border border-white/[0.06] bg-surface-2 px-2.5 max-md:order-last max-md:w-full md:w-[280px]">
          <Search size={14} aria-hidden="true" className="flex-none text-fg-3" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="İş ara veya NOV- numarası"
            aria-label="İş ara"
            className="min-w-0 flex-1 border-0 bg-transparent text-base text-fg-1 outline-none placeholder:text-fg-3 md:text-[13.5px]"
          />
        </label>
      </header>

      <div role="tablist" className="flex flex-none gap-6 border-b border-line px-4 pt-4 md:px-7">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={list === key}
            onClick={() => {
              setList(key);
              setStatus(null);
            }}
            className={`-mb-px border-0 border-b-2 bg-transparent px-0 pb-[11px] text-[14px] font-semibold ${list === key ? 'border-brand-500 text-fg-1' : 'border-transparent text-fg-3 hover:text-fg-1'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {(projectChips.length > 0 || isSprint) && (
        <div className="flex flex-none flex-wrap items-center gap-1.5 px-4 pt-3.5 md:px-7">
          <div role="group" aria-label="Proje" className="flex flex-wrap gap-1.5">
            {projectChips.map((p) => {
              const on = isSprint ? p.key === sprintProject : p.key === project;
              return (
                <button
                  key={p.key ?? 'all'}
                  type="button"
                  aria-pressed={on}
                  title={p.title}
                  onClick={() => {
                    setStatus(null);
                    if (!isSprint) return setPickedProject(p.key);
                    setSprintProject(p.key!);
                    setSprintId(null);
                    try {
                      localStorage.setItem(LAST_PROJECT, p.key!);
                    } catch {
                      // remembering the project is only a convenience
                    }
                  }}
                  className={projectChip(on)}
                >
                  {p.label}
                  {p.count !== undefined && <span className="font-medium tabular-nums opacity-60">{p.count}</span>}
                </button>
              );
            })}
          </div>
          {isSprint && (
            <>
              <span aria-hidden="true" className="mx-1.5 h-5 w-px bg-white/10" />
              {(sprints.data ?? []).map((s) => (
                <button key={s.id} type="button" aria-pressed={sprint?.id === s.id} onClick={() => setSprintId(s.id)} className={chip(sprint?.id === s.id)}>
                  {s.name}
                  {s.state === 'active' ? ' · aktif' : ''}
                </button>
              ))}
              <span className="flex-1" />
              <Toggle on={onlyMine} onChange={setOnlyMine} label="Yalnızca benimkiler" />
            </>
          )}
        </div>
      )}

      <div role="group" aria-label="Duruma göre süz" className="flex flex-none flex-wrap gap-1.5 px-4 pt-3 pb-2.5 md:px-7">
        {groups.length > 0 && (
          <button type="button" aria-pressed={status === null} onClick={() => setStatus(null)} className={chip(status === null)}>
            Tümü <span className="tabular-nums opacity-55">{scoped.length}</span>
          </button>
        )}
        {groups.map((g) => (
          <button key={g.name} type="button" aria-pressed={status === g.name} onClick={() => setStatus(g.name)} className={chip(status === g.name)}>
            <span aria-hidden="true" style={{ backgroundColor: jiraStatusColor(g.cards[0]!.status).dot }} className="size-[7px] rounded-full" />
            {g.name} <span className="tabular-nums opacity-55">{g.cards.length}</span>
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-6">
        <div aria-hidden="true" className={`sticky top-0 z-[2] hidden h-[34px] border-b border-line bg-surface-1 px-7 text-[11.5px] font-semibold tracking-[0.06em] text-fg-3 ${TABLE}`}>
          <span />
          <span>ANAHTAR</span>
          <span>BAŞLIK</span>
          <span>MODÜL</span>
          <span>ÜST İŞ</span>
          <span>ÖNCELİK</span>
        </div>
        {issues.isPending ? (
          <p className="m-0 px-7 py-6 text-sm text-fg-3">Yükleniyor…</p>
        ) : !issues.data ? (
          <p role="alert" className="m-0 px-7 py-6 text-sm text-[#EE9191]">
            {errorMessage(issues.error)}
          </p>
        ) : shown.length === 0 ? (
          <div className="px-8 py-16 text-center">
            <p className="m-0 text-base font-semibold">{q ? 'Sonuç bulunamadı' : list === 'reported' ? 'Açtığın açık iş yok' : isSprint ? 'Bu sprintte iş yok' : 'Sana atanmış açık iş yok'}</p>
            <p className="m-0 mt-1.5 text-[13.5px] text-fg-3">{q ? 'Aramayı veya süzgeci değiştirmeyi dene.' : 'Jira’daki işler burada listelenir.'}</p>
          </div>
        ) : (
          shown.map((g) => {
            const isCollapsed = collapsed.has(g.name);
            const color = jiraStatusColor(g.cards[0]!.status).dot;
            return (
              <section key={g.name} aria-label={`${g.name}, ${g.cards.length} iş`}>
                <button
                  type="button"
                  aria-expanded={!isCollapsed}
                  onClick={() => setCollapsed((before) => (before.has(g.name) ? new Set([...before].filter((n) => n !== g.name)) : new Set([...before, g.name])))}
                  className="flex h-11 w-full items-center gap-2 border-0 bg-transparent px-4 pt-2 text-left md:px-7"
                >
                  <ChevronDown size={14} aria-hidden="true" className={`flex-none text-fg-3 transition-transform max-md:order-last max-md:ml-auto ${isCollapsed ? '-rotate-90' : ''}`} />
                  <span aria-hidden="true" style={{ backgroundColor: color }} className="size-2 rounded-full" />
                  <span style={{ color }} className="text-[13.5px] font-semibold">
                    {g.name}
                  </span>
                  <span className="text-[13px] text-fg-3 tabular-nums">{q ? g.found.length : g.cards.length}</span>
                </button>
                {!isCollapsed && g.found.map((card) => <Row key={card.key} card={card} />)}
              </section>
            );
          })
        )}
      </div>
    </section>
  );
}
